/** Iconos de marca para la sección de canales (WebP optimizados + SVG para el resto). */

import type { ReactNode } from 'react';

type IconProps = { size?: number; className?: string };

function IconBox({ size = 40, className, children }: IconProps & { children: ReactNode }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      aria-hidden
      className={className}
      style={{ display: 'block', flexShrink: 0 }}
    >
      {children}
    </svg>
  );
}

function BrandChannelIcon({ name, size = 40, className }: IconProps & { name: string }) {
  const src = `/channels/${name}.webp`;
  return (
    <img
      src={src}
      alt=""
      width={size}
      height={size}
      loading="lazy"
      decoding="async"
      aria-hidden
      className={className}
      style={{
        display: 'block',
        flexShrink: 0,
        width: size,
        height: size,
        objectFit: 'contain',
      }}
    />
  );
}

export function IconWhatsapp({ size = 40, className }: IconProps) {
  return <BrandChannelIcon name="whatsapp" size={size} className={className} />;
}

export function IconMessenger({ size = 40, className }: IconProps) {
  return <BrandChannelIcon name="messenger" size={size} className={className} />;
}

export function IconInstagram({ size = 40, className }: IconProps) {
  return <BrandChannelIcon name="instagram" size={size} className={className} />;
}

export function IconTelegram({ size = 40, className }: IconProps) {
  return (
    <IconBox size={size} className={className}>
      <circle cx="12" cy="12" r="12" fill="#229ED9" />
      <path
        fill="#fff"
        fillRule="evenodd"
        clipRule="evenodd"
        d="M9.78 15.33l-.28 3.95c.4 0 .57-.17.78-.38l1.87-1.8 3.88 2.86c.71.39 1.22.18 1.41-.64l2.55-11.9c.24-1.08-.39-1.5-1.11-1.23L3.42 9.6c-1.07.42-1.06 1-.19 1.28l4.36 1.36 10.12-6.38c.47-.29.9-.13.55.18L9.78 15.33z"
      />
    </IconBox>
  );
}

export function IconSlack({ size = 40, className }: IconProps) {
  return <BrandChannelIcon name="slack" size={size} className={className} />;
}

export function IconTeams({ size = 40, className }: IconProps) {
  return <BrandChannelIcon name="teams" size={size} className={className} />;
}

export function IconWebWidget({ size = 40 }: IconProps) {
  return (
    <IconBox size={size}>
      <rect x="3" y="5" width="18" height="14" rx="2" fill="#0ea5e9" />
      <path fill="#e0f2fe" d="M7 10h10v1.5H7zm0 3h7v1.5H7z" />
    </IconBox>
  );
}

export function IconEmail({ size = 40 }: IconProps) {
  return (
    <IconBox size={size}>
      <rect x="2" y="4" width="20" height="16" rx="2" fill="#7c3aed" />
      <path fill="#ede9fe" d="M2 6l10 7 10-7v-2H2v2z" />
    </IconBox>
  );
}

export function IconSmsTwilio({ size = 40 }: IconProps) {
  return (
    <IconBox size={size}>
      <rect x="2" y="4" width="20" height="16" rx="2" fill="#F22F46" />
      <path fill="#fff" d="M6 8h12v1.5H6zm0 3h9v1.5H6z" />
    </IconBox>
  );
}

export type ChannelIconId =
  | 'whatsapp_meta'
  | 'messenger'
  | 'instagram'
  | 'telegram'
  | 'web_widget'
  | 'email'
  | 'sms_twilio'
  | 'slack'
  | 'teams';

const ICONS: Record<ChannelIconId, (p: IconProps) => ReactNode> = {
  whatsapp_meta: IconWhatsapp,
  messenger: IconMessenger,
  instagram: IconInstagram,
  telegram: IconTelegram,
  web_widget: IconWebWidget,
  email: IconEmail,
  sms_twilio: IconSmsTwilio,
  slack: IconSlack,
  teams: IconTeams,
};

export function ChannelIcon({ id, size = 40 }: { id: ChannelIconId; size?: number }) {
  const Cmp = ICONS[id];
  return <Cmp size={size} />;
}
